/**
 * iter278 — Hotel Menu upload card (owner-only).
 *
 * Lives inside Profile → Property Details, directly below the GST card.
 * Supports PDF + JPG/PNG/WEBP, 10 MB cap. Reuses the same
 * /api/property-settings/{id}/menu endpoints the mobile app uses.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, UploadCloud, Trash2, FileText } from "lucide-react";

const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";
const MAX_BYTES = 10 * 1024 * 1024;

const PropertyMenuCard = ({ propertyId }) => {
  const [menu, setMenu] = useState(null); // {menu_file_url, menu_file_name, menu_file_mime}
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const authHeader = () => {
    const t = localStorage.getItem("authToken") || localStorage.getItem("token") || "";
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(
        `${BASE}/api/property-settings/${propertyId}/menu`,
        { headers: authHeader() },
      );
      if (data?.STATUS === 1 && data.RESULT?.menu_file_url) {
        setMenu(data.RESULT);
      } else {
        setMenu(null);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load menu.");
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    load();
  }, [load]);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !propertyId) return;
    if (file.size > MAX_BYTES) {
      setError("Menu must be 10 MB or less.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(
        `${BASE}/api/property-settings/${propertyId}/menu`,
        form,
        { headers: { ...authHeader() } },
      );
      if (data?.STATUS === 1) setMenu(data.RESULT);
      else setError(data?.MESSAGE || "Upload failed.");
    } catch (ex) {
      setError(ex?.response?.data?.detail || ex?.message || "Upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    if (!propertyId || busy) return;
    setBusy(true);
    setError("");
    try {
      await axios.delete(
        `${BASE}/api/property-settings/${propertyId}/menu`,
        { headers: authHeader() },
      );
      setMenu(null);
    } catch (ex) {
      setError(ex?.response?.data?.detail || ex?.message || "Remove failed.");
    } finally {
      setBusy(false);
    }
  };

  const openMenu = async () => {
    if (!menu?.menu_file_url) return;
    // iter288 — The menu file route is auth-gated (`get_current_user` +
    // property-scope check), so `window.open()` on the raw URL will
    // fail — the browser does not attach our Bearer token on a cross-
    // frame GET. Fetch the file ourselves and open it from a blob URL.
    const url = menu.menu_file_url.startsWith("http")
      ? menu.menu_file_url
      : `${BASE}${menu.menu_file_url}`;
    try {
      const resp = await axios.get(url, {
        headers: authHeader(),
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(resp.data);
      const win = window.open(blobUrl, "_blank", "noopener,noreferrer");
      // Revoke the blob URL after the new tab has had a chance to grab
      // it; browsers tolerate this well past the open() call.
      setTimeout(() => {
        try {
          URL.revokeObjectURL(blobUrl);
        } catch {
          /* no-op */
        }
      }, 60_000);
      if (!win) {
        // Popup blocked — fall back to downloading via an anchor.
        const a = document.createElement("a");
        a.href = blobUrl;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.download = menu.menu_file_name || "menu";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to open menu.");
    }
  };

  return (
    <div
      className="bg-white rounded-2xl border border-gray-100 shadow-premium overflow-hidden"
      data-testid="profile-menu-card"
    >
      <div className="p-4 border-b border-gray-100 bg-[#EEF1FC]/50">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2 tracking-tight">
          <FileText className="w-4 h-4 text-[#1B2A7B]" />
          Hotel Menu
        </h3>
      </div>
      <div className="p-4 flex items-center gap-4">
        <div className="w-10 h-10 bg-[#EEF1FC] rounded-lg flex items-center justify-center">
          <FileText className="w-5 h-5 text-[#1B2A7B]" />
        </div>
        <div className="flex-1 min-w-0">
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : menu?.menu_file_url ? (
            <>
              <button
                onClick={openMenu}
                className="text-sm text-[#1B2A7B] font-medium underline truncate text-left max-w-full"
                data-testid="profile-menu-filename"
              >
                {menu.menu_file_name || "menu"}
              </button>
              <div className="flex gap-4 mt-1">
                <button
                  onClick={onPick}
                  disabled={busy}
                  className="text-xs text-[#1B2A7B] font-semibold disabled:opacity-50"
                  data-testid="profile-menu-replace"
                >
                  {busy ? "Working…" : "Replace"}
                </button>
                <button
                  onClick={onRemove}
                  disabled={busy}
                  className="text-xs text-red-600 font-semibold disabled:opacity-50"
                  data-testid="profile-menu-remove"
                >
                  Remove
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-500">No menu uploaded</p>
              <p className="text-xs text-gray-400 mt-0.5">PDF or image, max 10 MB</p>
            </>
          )}
          {error ? (
            <p className="text-xs text-red-600 mt-1">{error}</p>
          ) : null}
        </div>
        {!menu?.menu_file_url ? (
          <button
            onClick={onPick}
            disabled={busy || loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#1B2A7B] text-white text-sm font-semibold disabled:opacity-50"
            data-testid="profile-menu-upload"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <UploadCloud className="w-4 h-4" />
            )}
            Upload
          </button>
        ) : null}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          onChange={onFile}
          className="hidden"
          data-testid="profile-menu-input"
        />
      </div>
    </div>
  );
};

export default PropertyMenuCard;
